import { useTranslate } from "ra-core";

const W = 168;
const H = 40;
const PAD = 4;

/** 14-day single-series line (brand-600, 2 px), last point marked, native per-day tooltips via <title>. */
export const Sparkline = ({
  label,
  points,
}: {
  label: string;
  points: readonly { day: string; value: number }[];
}) => {
  const translate = useTranslate();
  const max = Math.max(1, ...points.map((p) => p.value));
  const step = points.length > 1 ? (W - 2 * PAD) / (points.length - 1) : 0;
  const xy = points.map(
    (p, i) =>
      [PAD + i * step, H - PAD - (p.value / max) * (H - 2 * PAD)] as const,
  );
  const total = points.reduce((s, p) => s + p.value, 0);
  const last = xy.at(-1);
  const hit = Math.max(step, 8);
  return (
    <div className="flex items-center gap-4">
      <svg
        role="img"
        aria-label={translate("gavion.dashboard.sparkline_aria", {
          label,
          total,
          days: points.length,
          _: `${label} : ${total} sur ${points.length} jours`,
        })}
        viewBox={`0 0 ${W} ${H}`}
        width={W}
        height={H}
        className="max-w-full overflow-visible"
      >
        <polyline
          points={xy.map(([x, y]) => `${x},${y}`).join(" ")}
          fill="none"
          className="stroke-brand-600"
          strokeWidth={2}
          strokeLinejoin="round"
          strokeLinecap="round"
        />
        {last && (
          <circle
            cx={last[0]}
            cy={last[1]}
            r={4}
            className="fill-brand-600 stroke-card"
            strokeWidth={2}
          />
        )}
        {points.map((p, i) => (
          <rect
            key={p.day}
            x={xy[i][0] - hit / 2}
            y={0}
            width={hit}
            height={H}
            fill="transparent"
          >
            <title>{`${p.day} : ${p.value}`}</title>
          </rect>
        ))}
      </svg>
      <ul aria-label={label} className="sr-only">
        {points.map((p) => (
          <li key={p.day}>{`${p.day} : ${p.value}`}</li>
        ))}
      </ul>
      <div aria-hidden="true">
        <p className="text-[11px] font-bold uppercase tracking-[.08em] text-muted-foreground">
          {label}
        </p>
        <p className="text-lg font-extrabold tabular-nums">{total}</p>
      </div>
    </div>
  );
};
